import {
  mapLocalAssetFiltersToV1,
  toLegacyDetail,
  toLegacyLocalAssetsList,
  v1Delete,
  v1Get,
  v1Post,
  v1Put,
} from '@/services/v1Api';

/**
 * Accessory Service
 * Handles API calls for asset accessories functionality
 */
export class AccessoryService {
    /**
     * Get accessories for an asset
     * @param {string|number} assetId - The asset ID
     * @returns {Promise<Array>} List of accessories
     */
    static async getAccessories(assetId) {
        try {
            const { data } = await v1Get(`/api/v1/local-assets/${assetId}/accessories`);
            return Array.isArray(data) ? data : (data?.accessories ?? []);
        } catch (error) {
            console.error('Error fetching accessories:', error);
            throw error;
        }
    }

    /**
     * Add an accessory to an asset
     * @param {string|number} assetId - The asset ID
     * @param {Object} accessoryData - Accessory data
     * @returns {Promise<Object>} Created accessory
     */
    static async addAccessory(assetId, accessoryData) {
        try {
            const { data } = await v1Post(`/api/v1/local-assets/${assetId}/accessories`, accessoryData);
            return toLegacyDetail(data);
        } catch (error) {
            console.error('Error adding accessory:', error);
            throw error;
        }
    }

    /**
     * Add multiple accessories to an asset
     * @param {string|number} assetId - The asset ID
     * @param {Array} accessoriesData - Array of accessory data
     * @returns {Promise<Array>} Created accessories
     */
    static async addAccessories(assetId, accessoriesData) {
        try {
            const promises = accessoriesData.map(accessoryData =>
                this.addAccessory(assetId, accessoryData)
            );
            return await Promise.all(promises);
        } catch (error) {
            console.error('Error adding accessories:', error);
            throw error;
        }
    }

    /**
     * Update an accessory
     * @param {string|number} assetId - The asset ID
     * @param {string|number} accessoryId - The accessory ID
     * @param {Object} accessoryData - Updated accessory data
     * @returns {Promise<Object>} Updated accessory
     */
    static async updateAccessory(assetId, accessoryId, accessoryData) {
        try {
            const { data } = await v1Put(
                `/api/v1/local-assets/${assetId}/accessories/${accessoryId}`,
                accessoryData
            );
            return toLegacyDetail(data);
        } catch (error) {
            console.error('Error updating accessory:', error);
            throw error;
        }
    }

    /**
     * Remove an accessory from an asset
     * @param {string|number} assetId - The asset ID
     * @param {string|number} accessoryId - The accessory ID
     * @returns {Promise<Object>} Confirmation response
     */
    static async removeAccessory(assetId, accessoryId) {
        try {
            await v1Delete(`/api/v1/local-assets/${assetId}/accessories/${accessoryId}`);
            return { success: true };
        } catch (error) {
            console.error('Error removing accessory:', error);
            throw error;
        }
    }

    /**
     * Get available assets that can be added as accessories
     * @param {string|number} assetId - The asset ID to exclude
     * @param {Object} filters - Optional filters
     * @returns {Promise<Array>} Available assets
     */
    static async getAvailableAssets(assetId, filters = {}) {
        try {
            const params = mapLocalAssetFiltersToV1({
                exclude_asset_id: assetId,
                per_page: 1000,
                ...filters
            });

            const { data, meta } = await v1Get('/api/v1/local-assets', params);
            const list = toLegacyLocalAssetsList(data, meta);
            return list.data || data || [];
        } catch (error) {
            console.error('Error fetching available assets:', error);
            throw error;
        }
    }

    /**
     * Get accessory types
     * @returns {Promise<Array>} List of accessory types
     */
    static async getAccessoryTypes() {
        try {
            const { data } = await v1Get('/api/v1/options/accessory-types');
            return Array.isArray(data) ? data : (data?.types ?? data ?? []);
        } catch (error) {
            console.error('Error fetching accessory types:', error);
            return [
                { id: 'mandatory', name: 'Mandatory', description: 'Required accessory' },
                { id: 'optional', name: 'Optional', description: 'Optional accessory' }
            ];
        }
    }

    /**
     * Validate accessory data
     * @param {Object} accessoryData - Accessory data to validate
     * @returns {Object} Validation result
     */
    static validateAccessoryData(accessoryData) {
        const errors = [];

        if (!accessoryData.local_asset_id) {
            errors.push('Local asset ID is required');
        }

        if (!accessoryData.accessory_local_asset_id) {
            errors.push('Accessory local asset ID is required');
        }

        if (!accessoryData.quantity || accessoryData.quantity < 1) {
            errors.push('Quantity must be at least 1');
        }

        if (!accessoryData.accessory_type) {
            errors.push('Accessory type is required');
        }

        return {
            isValid: errors.length === 0,
            errors: errors
        };
    }
}

export default AccessoryService;
